import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { CheckCircle, AlertTriangle, Info, X } from 'lucide-react'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastItem {
  id: number
  message: string
  type: ToastType
  detail?: string
  duration: number
  action?: { label: string; onClick: () => void }
}

interface ToastOptions {
  detail?: string
  duration?: number
  action?: { label: string; onClick: () => void }
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType, opts?: ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

const MAX_VISIBLE = 3

const STYLES: Record<ToastType, { box: string; icon: string }> = {
  success: { box: 'bg-green-50 border-green-200 text-green-800', icon: 'text-green-500' },
  warning: { box: 'bg-yellow-50 border-yellow-200 text-yellow-800', icon: 'text-yellow-500' },
  error: { box: 'bg-red-50 border-red-200 text-red-800', icon: 'text-red-500' },
  info: { box: 'bg-theme-primary-light border-theme-primary text-theme-primary', icon: 'text-theme-primary' },
}

function ToastCard({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, item.duration)
    return () => clearTimeout(timer)
  }, [item.duration, onClose])

  const Icon = item.type === 'success' ? CheckCircle : item.type === 'info' ? Info : AlertTriangle
  const style = STYLES[item.type]

  return (
    <div
      role={item.type === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg max-w-sm pointer-events-auto ${style.box}`}
    >
      <Icon size={18} className={`mt-0.5 shrink-0 ${style.icon}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{item.message}</p>
        {item.detail && <p className="text-xs mt-0.5 opacity-75">{item.detail}</p>}
        {item.action && (
          <button
            onClick={() => { item.action!.onClick(); onClose() }}
            className="text-xs font-semibold underline mt-1 hover:opacity-75"
          >
            {item.action.label}
          </button>
        )}
      </div>
      <button onClick={onClose} aria-label="Dismiss notification" className="p-0.5 rounded hover:bg-black/5 shrink-0">
        <X size={14} />
      </button>
    </div>
  )
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const toast = useCallback((message: string, type: ToastType = 'success', opts?: ToastOptions) => {
    const id = nextId.current++
    const duration = opts?.duration ?? (type === 'error' ? 6000 : 4000)
    setToasts(prev => [...prev, { id, message, type, detail: opts?.detail, duration, action: opts?.action }])
  }, [])

  const remove = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.slice(-MAX_VISIBLE).map(t => (
          <ToastCard key={t.id} item={t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}
